import { EntityData } from "@models/companies/entity-data";
import { IndividualData } from "@models/individuals/individual-data";
import { ShareholderRelationshipDetails } from "@models/relationships/relationship";
import { SecurityHolding } from "@models/shares/security-holding";
import { Address } from "../address/address";
import { nonBeneficialOwnerLabel } from "../../forms/asic-forms/shares/constants/non-beneficial-owner-label";

export class SecurityRegistryRecord {
  holders: (IndividualHolderModel | CorporateHolderModel)[];
  holdings: SecurityHolding[];
  nonAsic: boolean;
  validationError: AsicShareholderValidationError | null;

  constructor(record?: Partial<SecurityRegistryRecord>) {
    this.holders = record?.holders?.map(holder => {
      switch (holder.$type) {
        case 'IndividualHolderModel':
          return new IndividualHolderModel(holder);
        case 'CorporateHolderModel':
          return new CorporateHolderModel(holder);
        default:
          throw new Error(`Unknown holder type: ${ holder.$type }`);
      }
    }) ?? [];
    this.holdings = record?.holdings ? record.holdings.map(holding => new SecurityHolding(holding)) : [];
    this.nonAsic = record?.nonAsic ?? false;
    this.validationError = record?.validationError ? new AsicShareholderValidationError(record.validationError) : null;
  }

  get beneficialOwnerGroups(): Record<string, (IndividualHolderModel | CorporateHolderModel)[]> {
    const result = this.holders.reduce((result, item) => {
      const key = item.details.isBeneficialOwner ? '' : item.details.beneficialOwnerLabel;
      result[key] = [...result[key] || [], item];
      return result;
    }, {} as Record<string, (IndividualHolderModel | CorporateHolderModel)[]>);

    //sort result by type and name
    Object.keys(result).forEach(key => {
      result[key].sort((a, b) => {
        if (a instanceof IndividualHolderModel && b instanceof CorporateHolderModel) {
          return 1;
        }
        if (a instanceof CorporateHolderModel && b instanceof IndividualHolderModel) {
          return -1;
        }
        return a.name.localeCompare(b.name);
      });
    });

    return result;
  }

  get fullName(): string {
    const groups = Object.entries(this.beneficialOwnerGroups).sort(([a], [b]) => a.localeCompare(b));

    return groups.map(([key, value]) => {
      const names = value.map(item => item instanceof IndividualHolderModel ? item.individualData.fullNameLabel : `${ item.entityData.name }`);

      const nboSufix = !key ? '' : (key === 'NBO' || key == nonBeneficialOwnerLabel ? ` ${ nonBeneficialOwnerLabel }` : ` as trustee for ${ key }`);

      return `${ names.join(', ') }${ nboSufix }`;
    }).join('; ');
  }

  get beneficialOwner(): string {
    return Object.keys(this.beneficialOwnerGroups).sort((a, b) => a.localeCompare(b))[0] ?? '';
  }

  get isBeneficialOwner(): boolean {
    return this.holders.every(h => h.details.isBeneficialOwner);
  }

  get number(): number {
    return this.holdings.reduce((acc, holding) => acc + holding.number, 0);
  }

  get fullyPaid(): boolean {
    return this.holdings.every(holding => holding.fullyPaid);
  }

  get previousNumber(): number {
    return this.holdings.reduce((acc, holding) => acc + (holding?.previousNumber ?? 0), 0);
  }

  get name(): string {
    return this.holders.map((holder) => holder.name).join(', ') + ` (${ this.number } ${ this.holdings?.[0]?.securityType?.class ?? '' })`;
  }

  get addresses(): Address[] {
    return this.holders.map((holder) => {
      if (holder.$type == IndividualHolderModel.$type) {

        const individualHolder = holder as IndividualHolderModel;
        return individualHolder.individualData.address;
      }

      return (holder as CorporateHolderModel).entityData.registeredAddress;
    });
  }

  get authorisedSignatory(): string[][] {
    return this.holders
      .map((holder) => holder.details.authorisedSignatories)
      .filter(Boolean)
      .map((authorisedSignatories) => authorisedSignatories
        ?.map((authorisedSignatory) => authorisedSignatory?.fullName)
      ) as string[][];
  }

  isEqual(recordToCompare: SecurityRegistryRecord): boolean {
    return this.holders.length > 0
      && recordToCompare.holders.length > 0
      && this.holders.every((holder) => recordToCompare.holders.find((holderToCompare) => holderToCompare.relationshipId == holder.relationshipId))
      && recordToCompare.holders.every((holderToCompare) => this.holders.find((holder) => holderToCompare.relationshipId == holder.relationshipId));
  }
}

export abstract class HolderModelBase {
  static readonly $type: string;
  $type!: string;

  abstract get name(): string;
  abstract get address(): Address;

  relationshipId: string;
  isPosted: boolean;
  details: ShareholderRelationshipDetails;

  constructor(holder?: Partial<HolderModelBase>) {
    this.details = holder?.details ? new ShareholderRelationshipDetails(holder.details) : new ShareholderRelationshipDetails();
    this.$type = (this.constructor as typeof HolderModelBase).$type;
    this.relationshipId = holder?.relationshipId ?? '';
    this.isPosted = holder?.isPosted ?? false;
  }
}

export class CorporateHolderModel extends HolderModelBase {
  static override readonly $type = 'CorporateHolderModel';

  entityData: EntityData;
  entityId?: string;

  override get name(): string {
    return `${ this.entityData.name }`;
  }

  override get address(): Address {
    return this.entityData.registeredAddress;
  }

  constructor(holder?: Partial<CorporateHolderModel>) {
    super(holder);
    this.entityData = holder?.entityData ? new EntityData(holder.entityData) : new EntityData();
    this.entityId = holder?.entityId;
  }
}

export class IndividualHolderModel extends HolderModelBase {
  static override readonly $type = 'IndividualHolderModel';

  individualData: IndividualData;
  previousIndividualData?: IndividualData | null | undefined;
  pendingButtonState?: boolean | undefined;
  individualId?: string;

  override get name(): string {
    return this.individualData.fullNameLabel;
  }

  override get address(): Address {
    return this.individualData.address;
  }

  constructor(holder?: Partial<IndividualHolderModel>) {
    super(holder);
    this.individualData = holder?.individualData ? new IndividualData(holder.individualData) : new IndividualData();
    this.previousIndividualData = holder?.previousIndividualData ? new IndividualData(holder.previousIndividualData) : new IndividualData();
    this.pendingButtonState = holder?.pendingButtonState ?? false;
    this.individualId = holder?.individualId;
  }
}

export class AsicShareholderValidationError {
  totalNumber: number;
  class: string;
  asicTotalNumber: number;

  constructor(validationError?: Partial<AsicShareholderValidationError>) {
    this.totalNumber = validationError?.totalNumber ?? 0;
    this.class = validationError?.class ?? '';
    this.asicTotalNumber = validationError?.asicTotalNumber ?? 0;
  }
}
